import React from "react";
import { LivePulsingMarker } from "../../assets/Icons/mapIcons/LivePulsingMarker";
import type { VehicleOverlay } from "../../hooks/useLiveVehicles";
import { getLinePublicLabel } from "../../utils/lineDisplay";
import { getNetworkColor } from "../../utils/networkColor";
type Props = {
  overlaysByLine: Record<string, VehicleOverlay[]>;
  lineIds: string[];
  lineLabelById?: Record<string, string>;

  markerScale?: number;
  markerZoomScale?: number;
  markerScaleKey?: string;
};

function isFiniteLatLng(v: VehicleOverlay) {
  return Number.isFinite(v.lat) && Number.isFinite(v.lng);
}

function LiveTrafficLayerBase({
  overlaysByLine,
  lineIds,
  lineLabelById,
  markerScale = 1,
  markerZoomScale = 1,
  markerScaleKey = "",
}: Props) {
  const items = React.useMemo(() => {
    const out: Array<{ lineId: string; v: VehicleOverlay }> = [];

    for (const lid of lineIds) {
      const arr = overlaysByLine[lid] ?? [];

      for (const v of arr) {
        if (isFiniteLatLng(v)) {
          out.push({ lineId: lid, v });
        }
      }
    }

    return out;
  }, [overlaysByLine, lineIds]);

  const liveMarkerSize = React.useMemo(() => {
    const baseSize = 135;
    const minSize = 34 * markerScale;

    const rawSize = baseSize * markerScale * markerZoomScale;

    return Math.round(Math.max(minSize, rawSize));
  }, [markerScale, markerZoomScale]);

  if (items.length === 0) return null;

  const colorForLine = (lineId: string) => getNetworkColor(lineId);

  const labelForLine = (lineId: string) => {
    const raw = lineLabelById?.[lineId];

    if (raw) {
      const rawLabel = getLinePublicLabel(raw);
      if (rawLabel) return rawLabel;
    }

    return getLinePublicLabel(lineId) || String(lineId);
  };

  return (
    <>
      {items.map(({ lineId, v }) => (
        <LivePulsingMarker
          key={`${markerScaleKey}::${lineId}::${v.id}`}
          position={{ lat: v.lat, lng: v.lng }}
          size={liveMarkerSize}
          color={colorForLine(lineId)}
          ringCount={4}
          ringScale={0.5}
          label={labelForLine(lineId)}
          bearingDeg={typeof v.bearing === "number" ? v.bearing : 0}
          keepTextUpright
          clickable={false}
        />
      ))}
    </>
  );
}

const LiveTrafficLayer = React.memo(LiveTrafficLayerBase);

export default LiveTrafficLayer;
